import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../i18n/LanguageContext";
import "./ai-diagnostic.css";

const API_URL = process.env.REACT_APP_AI_DIAGNOSTIC_URL || "";
const PUBLIC_KEY = process.env.REACT_APP_PUBLIC_WEBHOOK_KEY || "";

export default function AiDiagnostic() {
  const navigate = useNavigate();
  const { tr, lang } = useLanguage();

  const [role, setRole] = useState("");
  const [description, setDescription] = useState("");
  const [problemList, setProblemList] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecs, setSelectedRecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canGenerate = role.trim() && description.trim();

  const handleGenerate = async () => {
    if (!API_URL) {
      setError("API URL not configured.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setProblemList([]);
      setRecommendations([]);

      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": PUBLIC_KEY || "",
        },
        body: JSON.stringify({
          role: role.trim(),
          description: description.trim(),
          language: lang, // 🔥 kirim language ke backend
        }),
      });

      let data = null;
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        data = { error: await res.text() };
      }

      if (!res.ok) {
        const message = data?.error || `HTTP ${res.status}`;
        throw new Error(message);
      }

      if (data.error) {
        setError(data.error);
        return;
      }

      setProblemList(Array.isArray(data.problems) ? data.problems : []);
      setRecommendations(
        Array.isArray(data.recommendations) ? data.recommendations : []
      );
      setSelectedRecs([]);
    } catch (err) {
      console.error("API error:", err);
      setError(
        err?.message
          ? tr(
              `Something went wrong: ${err.message}`,
              `Terjadi kesalahan: ${err.message}`
            )
          : tr(
              "Something went wrong. Please try again.",
              "Terjadi kesalahan. Silakan coba lagi."
            )
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleRecommendation = (rec) => {
    setSelectedRecs((prev) =>
      prev.includes(rec)
        ? prev.filter((item) => item !== rec)
        : [...prev, rec]
    );
  };

  const handleInputToForm = () => {
    const payload = {
      role,
      description,
      recommendations:
        selectedRecs.length > 0 ? selectedRecs : recommendations,
    };

    localStorage.setItem("aiDiagnosticDraft", JSON.stringify(payload));
    navigate("/#contact-us");
  };

  const getCapabilityRoute = (capability) => {
    if (typeof capability !== "string") return "";
    const match = capability.match(/^capability-(\d{2})$/);
    if (!match) return "";
    return `/#solutions-${match[1]}`;
  };

  return (
    <section className="ai-diagnostic-page">
      <h1 className="ai-diagnostic-title">
        {tr(
          "Define Your Problem to Make Solution",
          "Definisikan Masalahmu untuk Membuat Solusi"
        )} ✨
      </h1>

      <div className="ai-diagnostic-grid">
        <div className="ai-card ai-card-tall">
          <div className="ai-chip">
            {tr("Select Your Role", "Pilih Role Kamu")}
          </div>

          <input
            type="text"
            className="ai-role-input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder={tr(
              "Your role (e.g. Content Creator)",
              "Role kamu (contoh: Content Creator)"
            )}
          />

          <textarea
            className="ai-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
            placeholder={tr(
              "Describe your workflow in detail",
              "Jelaskan alur kerjamu secara detail."
            )}
          />

          <button
            className="ai-primary-btn"
            disabled={!canGenerate || loading}
            onClick={handleGenerate}
          >
            {loading
              ? tr("Generating...", "Sedang memproses...")
              : tr("Generate", "Generate")}
          </button>

          {error && <div className="ai-error">{error}</div>}
        </div>

        <div className="ai-right-stack">
          <div className="ai-card">
            <div className="ai-chip">
              {tr("Potential Problem", "Masalah Potensial")}
            </div>

            <ul className="ai-list">
              {problemList.length === 0 ? (
                <li className="ai-muted">
                  {tr(
                    "Input your data and problem first.",
                    "Masukkan data dan masalah terlebih dahulu."
                  )}
                </li>
              ) : (
                problemList.map((item, index) => (
                  <li key={index}>
                    <span className="ai-list-icon">⛔</span>
                    {tr(item.en, item.id)}
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="ai-card">
            <div className="ai-chip">
              {tr("Potential Solution", "Solusi Potensial")}
            </div>

            <div className="ai-checklist">
              {recommendations.map((rec, index) => {
                const linkRoute = getCapabilityRoute(rec?.capability);

                return (
                  <label key={index} className="ai-check-item">
                  <input
                    type="checkbox"
                    checked={selectedRecs.includes(rec)}
                    onChange={() => toggleRecommendation(rec)}
                  />
                  <span>
                    {linkRoute ? (
                      <a
                        href={linkRoute}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ai-solution-link"
                      >
                        {tr(rec.en, rec.id)}
                      </a>
                    ) : (
                      <span>{tr(rec.en, rec.id)}</span>
                    )}
                  </span>
                  </label>
                );
              })}
            </div>

            {recommendations.length > 0 && (
              <button
                className="ai-primary-btn"
                onClick={handleInputToForm}
              >
                {tr("Input to Form", "Input ke Form")}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}